import { getLoginConfig } from '@/helper'

const DEFAULT_LOGIN_CONFIG = {
  branding: {
    logo: null,                     // URL or require path; null = use default brandName logic
    title: '',                      // heading text; empty = no extra title shown
    subtitle: '',                   // sub-heading text
  },
  theme: {
    backgroundColor: '',            // empty = keep video background
    backgroundImage: '',            // URL to a background image (replaces video when set)
    backgroundOverlayColor: '',     // overlay on top of bg image
    cardBackgroundColor: '',        // login card bg
    cardBorderRadius: '',           // card corner radius
    cardOpacity: '80%',             // card opacity (default matches current 80%)
    primaryColor: '',               // primary / accent color
    primaryTextColor: '',           // text on primary-colored elements
    fontFamily: '',                 // CSS font-family override
    titleFontSize: '',              // heading font size
    maxCardWidth: '400px',          // max width of the login card
  },
  features: {
    showVideo: true,                // show/hide the video background
    showPoweredBy: true,            // show "powered by Carnot Innovations" footer
  },
  footer: {
    text: '',
    links: [],                      // [{ label: 'Privacy', url: '/privacy' }]
  },
}

/**
 * Deep-merge source into target (non-destructive).
 * Arrays and primitives in source overwrite target.
 */
function deepMerge(target, source) {
  if (!source || typeof source !== 'object') return target
  const result = { ...target }
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object'
    ) {
      result[key] = deepMerge(target[key], source[key])
    } else if (source[key] !== undefined) {
      result[key] = source[key]
    }
  }
  return result
}

const loginConfigStore = {
  state: {
    loginConfig: { ...DEFAULT_LOGIN_CONFIG },
    loginConfigLoaded: false,
    loginConfigLoading: false,
  },

  mutations: {
    setLoginConfig(state, config) {
      state.loginConfig = config
    },
    setLoginConfigLoaded(state, val) {
      state.loginConfigLoaded = val
    },
    setLoginConfigLoading(state, val) {
      state.loginConfigLoading = val
    },
  },

  actions: {
    async loadLoginConfig({ commit }) {
      commit('setLoginConfigLoading', true)
      try {
        const data = await getLoginConfig()
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          const merged = deepMerge(DEFAULT_LOGIN_CONFIG, data)
          commit('setLoginConfig', merged)
        }
        // else: keep defaults — API returned empty or null
      } catch (e) {
        console.warn('Failed to load login config, using defaults:', e)
      } finally {
        commit('setLoginConfigLoading', false)
        commit('setLoginConfigLoaded', true)
      }
    },
  },

  getters: {
    loginConfig: (state) => state.loginConfig,
    loginConfigLoaded: (state) => state.loginConfigLoaded,
    loginConfigLoading: (state) => state.loginConfigLoading,

    /**
     * Returns a flat object of CSS custom properties derived from config.theme.
     * Only includes properties that have non-empty values.
     */
    loginCssVariables: (state) => {
      const t = state.loginConfig.theme || {}
      const vars = {}
      if (t.backgroundColor)       vars['--login-bg-color'] = t.backgroundColor
      if (t.backgroundImage)        vars['--login-bg-image'] = `url('${t.backgroundImage}')`
      if (t.backgroundOverlayColor) vars['--login-bg-overlay'] = t.backgroundOverlayColor
      if (t.cardBackgroundColor)    vars['--login-card-bg'] = t.cardBackgroundColor
      if (t.cardBorderRadius)       vars['--login-card-radius'] = t.cardBorderRadius
      if (t.cardOpacity)            vars['--login-card-opacity'] = t.cardOpacity
      if (t.primaryColor)           vars['--login-primary'] = t.primaryColor
      if (t.primaryTextColor)       vars['--login-primary-text'] = t.primaryTextColor
      if (t.fontFamily)             vars['--login-font'] = t.fontFamily
      if (t.titleFontSize)          vars['--login-title-size'] = t.titleFontSize
      if (t.maxCardWidth)           vars['--login-max-width'] = t.maxCardWidth
      return vars
    },
  },
}

export default loginConfigStore
